import { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { F, T, haptic } from '../theme.jsx';
import { WIDGETS, defaultLayoutForRole } from '../widgets.jsx';
import { extractTrackingNumber } from './scanner.jsx';
import { useToast } from '../components/boothly/toast-provider.jsx';
import { getSeedOrders, getLoadingOverride } from '../test-helpers/seed.js';

// ── Shared constants (MUSS mit worker-api.js ROLE_PERMS sync sein) ────
const ROLE_PERMS = {
  owner:       ["read","write","delete","finance","manage_members","manage_roles","manage_store","manage_catalog"],
  admin:       ["read","write","delete","finance","manage_members","manage_catalog"],
  fulfillment: ["read","write"],
};
const TABS         = ["Alle", "Bereit", "Versendet", "Retoure"];

// ── Label upload/download utilities ─────────────────────────────────────
function labelBaseUrl(storeId, token) {
  if (storeId && token) return `/api/stores/${storeId}/labels/`;
  return "/api/labels/";
}
function labelHeaders(token) {
  if (token) return { "Authorization": `Bearer ${token}` };
  return {};
}
async function uploadLabel(file, orderId, storeId, token) {
  if (!file) throw new Error("Keine Datei");
  const sizeBytes = file.size || 0;
  if (sizeBytes > 10 * 1024 * 1024) {
    throw new Error(`Datei zu groß (${(sizeBytes / 1024 / 1024).toFixed(1)} MB, max 10 MB)`);
  }
  const ext = (file.name?.split?.(".")?.pop?.() || "bin").toLowerCase();
  const safeExt = ["pdf", "png", "jpg", "jpeg", "webp", "heic"].includes(ext) ? ext : "bin";
  const key = `order-${orderId}.${safeExt}`;
  const url = labelBaseUrl(storeId, token) + key;
  const mime = file.type || "application/octet-stream";
  let buffer;
  try { buffer = await file.arrayBuffer(); }
  catch (e) { throw new Error(`Datei konnte nicht gelesen werden: ${e.message}`); }
  function xhrPut(url, body, mime, token) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      else xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", mime);
      xhr.timeout = 60_000;
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: xhr.status, body: xhr.responseText });
        } else {
          let detail = xhr.responseText;
          try { const d = JSON.parse(detail); detail = d?.reason || d?.error || detail; } catch {}
          reject(new Error(`HTTP ${xhr.status} — ${detail || "no body"}`));
        }
      };
      xhr.onerror = () => reject(new Error("network error"));
      xhr.ontimeout = () => reject(new Error("timeout"));
      xhr.onabort = () => reject(new Error("aborted"));
      xhr.send(body);
    });
  }
  let lastError = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const blob = new Blob([buffer], { type: mime });
      await xhrPut(url, blob, mime, token);
      return { key, mime };
    } catch (e) {
      lastError = e;
      const msg = String(e?.message || "");
      const isNetwork = /network|timeout|aborted|load failed/i.test(msg);
      if (!isNetwork || attempt === 3) {
        const url_short = url.replace(/^https?:\/\/[^/]+/, "");
        throw new Error(`${msg} (PUT ${url_short}, ${(buffer.byteLength / 1024).toFixed(0)} KB, ${attempt}× XHR-versucht)`);
      }
      await new Promise(r => setTimeout(r, 400 * attempt));
    }
  }
  throw lastError || new Error("Upload fehlgeschlagen");
}
async function getLabelUrl(key, storeId, token) {
  const res = await fetch(labelBaseUrl(storeId, token) + key + "?signed=1", {
    credentials: token ? "omit" : "include",
    headers: labelHeaders(token),
  });
  if (!res.ok) throw new Error(`Label-URL fehlgeschlagen: HTTP ${res.status}`);
  const data = await res.json();
  return data.url;
}
async function deleteLabel(key, storeId, token) {
  if (!key) return;
  try {
    await fetch(labelBaseUrl(storeId, token) + key, {
      method: "DELETE",
      credentials: token ? "omit" : "include",
      headers: labelHeaders(token),
    });
  } catch (e) { console.warn("Label-Delete fehlgeschlagen:", e); }
}

// ── Form helpers ─────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);
// Online-Store Umbau Phase 1: Bestellung = Kanal (channelId, neue channels-Tabelle) +
// Produktzeilen (items: [{ productId, quantity }]). kanal (Legacy-String) bleibt für
// Übergang/Anzeige erhalten. channelId/items werden in openNew() befüllt.
const emptyForm = (channelId = null) => ({ bestellnummer: "", kanal: "", channelId, items: [], datum: today(), status: "Offen", notiz: "", trackingNumber: "", retoureErhalten: false, adresse: "", labelKey: "", labelMime: "" });

// ─────────────────────────────────────────────────────────────
// useOnlineStore — all state + logic for the online store
// Props: currentStore, currentRole, authToken, authUser, dark, setDark,
//        onBack, onStoreDeleted, userStores,
//        isMobile, isTablet, isDesktop, listW, containerMaxW
// ─────────────────────────────────────────────────────────────
export function useOnlineStore({
  currentStore, currentRole, authToken, authUser, dark, setDark, onBack, onStoreDeleted, userStores,
  isMobile, isTablet, isDesktop, listW, containerMaxW,
}) {
  const _toast = useToast();
  // Derived from props (replaces App()-level userEmail/identityLoaded state)
  const userEmail = authUser?.email || null;
  const identityLoaded = true; // Always true when OnlineStore is rendered

  // Online-Store Umbau Phase 3: Kanäle leben ausschließlich in der channels-Tabelle
  // (state `channels`, weiter unten). Der Legacy-JSONB stores.channels + DEFAULT_CHANNELS
  // (eBay/Etsy) sind entfernt. status_labels bleibt ein eigenes Store-Config-Feature.
  const DEFAULT_STATUS_LABELS = { Offen: "Offen", Bereit: "Bereit", Versendet: "Versendet", Retoure: "Retoure" };
  const [storeStatusLabels, setStoreStatusLabels] = useState(DEFAULT_STATUS_LABELS);
  const [showChannelsConfig, setShowChannelsConfig]         = useState(false);
  const [showStatusLabelsConfig, setShowStatusLabelsConfig] = useState(false);
  const [expandedChannelId, setExpandedChannelId]           = useState(null);

  // Deep-Link aus dem BatchSlotEditModal: wenn beim Öffnen des Channels-Managers
  // bereits ein expandedChannelId gesetzt ist (vom Slot-"Kanal-Einstellungen"-Link),
  // nach dem Render zur Zeile scrollen.
  useEffect(() => {
    if (!showChannelsConfig || !expandedChannelId) return;
    const id = expandedChannelId;
    setTimeout(() => {
      const el = document.getElementById(`channel-row-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 360);
  }, [showChannelsConfig, expandedChannelId]);

  // Activity Log (Owner/Admin) — Modal-State, Filter, Cursor-Pagination
  // ── Dashboard / Bottom-Nav ────────────────────────────────
  // Top-level view switch on mobile/tablet. Desktop stays on existing layout
  // (currentView === "orders") but a Dashboard-button in the header toggles.
  // Default: dashboard (User sieht beim Öffnen immer das Dashboard zuerst).
  const [currentView, setCurrentView]               = useState("dashboard"); // "dashboard" | "orders" | "more"
  // currentMode (Multi-Mode-Architektur, Migration 008):
  //   'online' → E-Commerce-Workflow (Default für bestehende Stores)
  //   'flea'   → Trödel-Hub
  // Wird in selectStore() aus storeWithRole.type gesetzt.
  const [currentMode, setCurrentMode] = useState("online");
  // Active sessions für flea-Stores des Users (für Landingpage-Anzeige).
  // Map<storeId, sessionObj | null>. Wird beim Landingpage-Mount gefüllt.
  const [activeSessions, setActiveSessions] = useState({});
  const [storeDashboardLayout, setStoreDashboardLayout] = useState(null);
  const [showCustomizeDashboard, setShowCustomizeDashboard] = useState(false);
  const [dashboardEditMode, setDashboardEditMode]   = useState(false); // long-press triggers wiggle/DnD

  const [showAuditLog, setShowAuditLog]             = useState(false);
  const [auditEntries, setAuditEntries]             = useState([]);
  const [auditFilter, setAuditFilter]               = useState({ entity_type: "all", days: 7 });
  const [auditLoading, setAuditLoading]             = useState(false);
  const [auditHasMore, setAuditHasMore]             = useState(false);

  // Scan-Modus (Versand-Workflow Phase 3b) — mobile-only barcode scanner
  const [showScanner, setShowScanner]               = useState(false);
  const [scannerState, setScannerState]             = useState("idle"); // idle | scanning | match | no_match | error
  const [scannerError, setScannerError]             = useState(null);
  const [lastMatchedOrder, setLastMatchedOrder]     = useState(null);
  const [lastUnmatchedCode, setLastUnmatchedCode]   = useState(null);
  const scannerControlsRef = useRef(null);
  const lastScanCodeRef    = useRef(null);   // dedup: same code within cooldown ignored
  const lastScanTimeRef    = useRef(0);
  const overlayTimerRef    = useRef(null);

  // Members & invitations management
  const [showMembers, setShowMembers]               = useState(false);
  const [membersList, setMembersList]               = useState([]);
  const [invitationsList, setInvitationsList]       = useState([]);
  const [memberSheetTab, setMemberSheetTab]         = useState("members"); // "members" | "invites"
  const [newInviteRole, setNewInviteRole]           = useState("fulfillment");
  const [createdInviteCode, setCreatedInviteCode]   = useState(null);
  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState(null);
  const [memberLoading, setMemberLoading]           = useState(false);

  // Permission check helper — always safe to call even before role is loaded
  function can(perm) { return (ROLE_PERMS[currentRole] || []).includes(perm); }

  // ── Display helpers (channel/status name lookup, fallback-safe) ──
  // Online-Store Umbau Phase 3: Kanäle kommen aus der channels-Tabelle (state
  // `channels`, Shape via channelFromDb: { id, name, feePercent, shippingCost }).
  // Aufrufer übergeben die channel_id der Bestellung (o.channelId), nicht mehr den
  // Legacy-kanal-Slug.
  function getChannelLabel(channelId) {
    if (!channelId) return "";
    return channels.find(c => c.id === channelId)?.name || "";
  }
  function getStatusLabel(status) {
    return storeStatusLabels[status] || status;
  }
  // Reserved-Pools entfallen in Phase 3 (kein type mehr). Alle Tabellen-Kanäle sind
  // Verkaufskanäle. Server liefert sie bereits nach created_at.asc sortiert.
  function getActiveChannels() { return channels.slice(); }
  function getAllChannels()    { return channels.slice(); }

  // ── Per-channel finance (Gebühr % + Versand) aus der channels-Tabelle ──
  // Kein price/fee_fixed mehr (Preis lebt am Produkt; fee_fixed nicht modelliert).
  function getChannelFinance(channelId) {
    const ch = channels.find(c => c.id === channelId);
    return {
      fee_percent: Number.isFinite(ch?.feePercent)   ? ch.feePercent   : 0,
      shipping:    Number.isFinite(ch?.shippingCost) ? ch.shippingCost : 0,
    };
  }
  // Online-Store Umbau Phase 1: Umsatz/Kosten/Gewinn pro Bestellung aus den
  // gesnapshotteten order_items + den Order-Snapshots (fee_percent/shipping).
  // Ersetzt die fixe 27,99-/Kanal-Finance-Logik. fee_percent in Prozent (0–100).
  function getOrderTotals(o) {
    const items = o?.orderItems || [];
    const revenue  = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
    const cogs     = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unitCost)  || 0), 0);
    const feePct   = Number(o?.feePercentSnapshot)   || 0;
    const shipping = Number(o?.shippingCostSnapshot) || 0;
    const fee      = revenue * feePct / 100;
    const profit   = revenue - cogs - fee - shipping;
    return { revenue, cogs, fee, shipping, profit };
  }
  function getOrderRevenue(o)  { return getOrderTotals(o).revenue; }
  function getOrderCogs(o)     { return getOrderTotals(o).cogs; }
  function getOrderShipping(o) { return getOrderTotals(o).shipping; }
  function getOrderFee(o)      { return getOrderTotals(o).fee; }
  function getOrderProfit(o)   { return getOrderTotals(o).profit; }
  // Slugify channel name → channel id (lowercase, alphanumeric + dash)
  function slugifyChannelName(name) {
    return (name || "")
      .toLowerCase()
      .normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32) || `channel-${Date.now()}`;
  }

  async function saveStoreConfig(patch) {
    if (!currentStore || !authToken) return false;
    const res = await fetch(`/api/stores/${currentStore.id}/config`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      showToast(d.reason || "Speichern fehlgeschlagen", "error");
      return false;
    }
    const updated = await res.json();
    // Phase 3: saveStoreConfig pflegt nur noch status_labels (Kanäle leben in der
    // channels-Tabelle, siehe apiChannel*). Der channels-Zweig ist entfernt.
    if (updated.status_labels) setStoreStatusLabels(updated.status_labels);
    return true;
  }

  // Kanal löschen (channels-Tabelle). Der Worker (handleDeleteChannel) blockt mit
  // 409, solange Bestellungen den Kanal referenzieren — wir melden das sauber und
  // lassen die Liste unverändert. Bei Erfolg lokal aus `channels` entfernen.
  async function apiChannelDelete(channelId) {
    if (!currentStore || !authToken) return false;
    const ch = channels.find(c => c.id === channelId);
    const res = await fetch(`/api/stores/${currentStore.id}/channels/${channelId}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (res.status === 409) {
      const d = await res.json().catch(() => ({}));
      showToast(d.reason || "Kanal wird von Bestellungen genutzt und kann nicht gelöscht werden.", "warning");
      return false;
    }
    if (!res.ok) { showToast("Löschen fehlgeschlagen", "error"); return false; }
    setChannels(prev => prev.filter(c => c.id !== channelId));
    showToast(`"${ch?.name || "Kanal"}" gelöscht`, "success");
    haptic(15);
    return true;
  }

  // Kanal einer bestehenden Bestellung wechseln. Optimistisch channelId + die
  // Gebühr/Versand-Snapshots aus der channels-Row setzen; der Worker re-snapshottet
  // autoritativ (handleUpdateOrder). Geld-Pfad — bewusst eigener Helfer statt
  // updateOrder mit 'kanal' zu überladen.
  function changeOrderChannel(orderId, channelId) {
    const ch = channels.find(c => c.id === channelId);
    const patch = {
      channelId: channelId ?? null,
      feePercentSnapshot:   ch ? (Number(ch.feePercent)   || 0) : 0,
      shippingCostSnapshot: ch ? (Number(ch.shippingCost) || 0) : 0,
      kanal: ch ? ch.name : "",
    };
    setOrders(o => o.map(x => x.id === orderId ? { ...x, ...patch } : x));
    apiOrderUpdate(orderId, { channelId: channelId ?? null });
  }

  // ── Member-Management API helpers ─────────────────────────
  async function loadMembersAndInvitations() {
    if (!currentStore || !authToken) return;
    setMemberLoading(true);
    try {
      const headers = { "Authorization": `Bearer ${authToken}` };
      const [memRes, invRes] = await Promise.all([
        fetch(`/api/stores/${currentStore.id}/members`, { headers }),
        fetch(`/api/stores/${currentStore.id}/invitations`, { headers }),
      ]);
      if (memRes.ok) setMembersList(await memRes.json());
      if (invRes.ok) setInvitationsList(await invRes.json());
    } catch (e) { console.error("loadMembers failed:", e); }
    setMemberLoading(false);
  }

  async function createInvitation(role) {
    const res = await fetch(`/api/stores/${currentStore.id}/invitations`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify({ role }),
    });
    if (res.ok) {
      const inv = await res.json();
      setCreatedInviteCode(inv.code);
      loadMembersAndInvitations();
    } else {
      showToast("Einladung fehlgeschlagen", "error");
    }
  }

  async function revokeInvitation(invId) {
    const res = await fetch(`/api/stores/${currentStore.id}/invitations/${invId}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (res.ok) {
      showToast("Einladung widerrufen");
      loadMembersAndInvitations();
    } else {
      const d = await res.json().catch(() => ({}));
      showToast(d.reason || "Widerrufen fehlgeschlagen", "error");
    }
  }

  async function changeMemberRole(targetUserId, newRole) {
    const res = await fetch(`/api/stores/${currentStore.id}/members/${targetUserId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify({ role: newRole }),
    });
    if (res.ok) loadMembersAndInvitations();
    else { const d = await res.json(); showToast(d.reason || "Rollenwechsel fehlgeschlagen", "error"); }
  }

  async function removeMember(targetUserId) {
    const res = await fetch(`/api/stores/${currentStore.id}/members/${targetUserId}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (res.ok) {
      setConfirmRemoveUserId(null);
      loadMembersAndInvitations();
    } else { const d = await res.json(); showToast(d.reason || "Entfernen fehlgeschlagen", "error"); }
  }

  const [orders, setOrders]               = useState([]);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [ordersCursor, setOrdersCursor]   = useState(null);
  // Online-Store Umbau Phase 1: Produktkatalog + Kanäle (neue Tabellen, getrennt
  // vom Legacy-JSONB storeChannels). seededRef verhindert doppeltes Default-Seeding.
  const [products, setProducts]           = useState([]);
  const [channels, setChannels]           = useState([]);
  const seededRef                         = useRef(false);
  const [showSheet, setShowSheet]         = useState(false);
  const [showDetail, setShowDetail]       = useState(false);
  const [detailOrder, setDetailOrder]     = useState(null);
  const [detailDeleteConfirm, setDetailDeleteConfirm] = useState(false);
  const [form, setForm]                   = useState(emptyForm());
  const [tabIndex, setTabIndex]           = useState(0); // Default: Alle-Tab
  const [filterKanal, setFilterKanal]     = useState("Alle");
  const [showKanalFilter, setShowKanalFilter] = useState(false);
  const [search, setSearch]               = useState("");
  const [showSearch, setShowSearch]       = useState(false);
  // Tracking-Confirm-Modal — gezeigt nach OCR-Extraktion damit User
  // den richtigen Kandidaten wählen oder manuell eingeben kann.
  // Shape: { orderId, candidates, ocrText, manualValue } | null
  const [trackingConfirm, setTrackingConfirm] = useState(null);
  // FulfillmentSweepModal entfernt — Versand läuft jetzt ausschliesslich über
  // den Scanner (Live-Camera + manueller Photo-Fallback). Single source of truth.
  const [showSettings, setShowSettings]   = useState(false);
  const [confirmDeleteStoreOpen, setConfirmDeleteStoreOpen] = useState(false);
  // Kanal-Filter-Picker (kleines Sheet aus Settings-Panel-Row)
  const [filterPickerOpen, setFilterPickerOpen] = useState(false);
  const [sortBy, setSortBy]               = useState(() => {
    try { return localStorage.getItem("memorica_sortBy") || "datum_desc"; } catch { return "datum_desc"; }
  });
  // Datum-Bereich-Filter: 30, 90 oder -1 (alles). Nur auf abgeschlossene (Versendet/Retoure) angewandt.
  const [dateRange, setDateRange]         = useState(() => {
    try { const v = parseInt(localStorage.getItem("memorica_dateRange") || "30", 10); return isNaN(v) ? 30 : v; } catch { return 30; }
  });
  const [sendungOrder, setSendungOrder]   = useState(null);
  const [showStats, setShowStats]         = useState(false);
  const [chartMode, setChartMode]         = useState("weeks");
  const [settingsDragY, setSettingsDragY] = useState(0);
  // addDragY/addStartY entfernt — Sheet ist jetzt top-anchored, Swipe-to-close
  // war an Bottom-Sheet-Layout gekoppelt und nicht mehr nötig.
  const [goal, setGoal]                   = useState(100);
  const [showGoalEdit, setShowGoalEdit]   = useState(false);
  // Legacy `batchInvestment` State + InvestEdit-Popup wurden entfernt.
  // Investment kommt jetzt ausschliesslich pro Batch (Lager-Modal). Die Summe
  // aller Batch-Investments fließt als `totalInvestment` in den Cashflow ein.
  const [showGuvDetails, setShowGuvDetails]   = useState(false);
  const [batches, setBatches]                 = useState([]);
  const [showLager, setShowLager]             = useState(false);
  const [lagerDragY, setLagerDragY]           = useState(0);
  // Phase 2: Produktkatalog-Sheet + Lager auf ein Produkt gescoped
  const [showCatalog, setShowCatalog]         = useState(false);
  // Preis-Kalkulator-Sheet (reines UI, kein State-Backend)
  const [showCalculator, setShowCalculator]   = useState(false);
  const [lagerProductId, setLagerProductId]   = useState(null);
  const [editBatchId, setEditBatchId]         = useState(null);
  const [newBatchOpen, setNewBatchOpen]       = useState(false);
  const [nbName, setNbName]                   = useState("");
  // String-States damit User leer/mehrstellig tippen können (vorher coercion → Tastatur weg).
  const [nbStueck, setNbStueck]               = useState("100");
  const [nbInvest, setNbInvest]               = useState("1000");
  const [editPool, setEditPool]               = useState(null); // { batchId, kanal }
  // Set<batchId> der aktuell eingeklappten Batches im Lager-Tab.
  // Bewusst nicht persistiert — gilt nur für die aktuelle Session.
  const [collapsedBatches, setCollapsedBatches] = useState(() => new Set());
  // Channel-Verwaltung — nach Cleanup (Pass 1):
  //   • Anlegen + alle globalen Settings: Channels-Manager (Settings → Verkaufskanäle)
  //   • Pro-Batch Pool/Threshold: BatchSlotEditModal (Lager-Slot-Click)
  //   • Löschen: über die Kanal-Verwaltung (Settings → Verkaufskanäle) via apiChannelDelete
  // editChannelInBatch: { batchId, channelId } | null → BatchSlotEditModal (Legacy)
  const [editChannelInBatch, setEditChannelInBatch] = useState(null);
  // Add-Channel-Modal Status — replaces alte prompt+confirm-Sequenz.
  const [newChannelOpen, setNewChannelOpen]         = useState(false);
  const [confirmNoSendung, setConfirmNoSendung] = useState(false);
  const [confirmRetoure, setConfirmRetoure]     = useState(null); // order id pending
  // Bereit → Versendet Workflow:
  //   shipChoice   = ShipChoiceModal offen (Order-Obj). Erste Stufe: Scan vs. Manuell.
  //   manualShip   = ManualShipConfirmModal offen. Zweite Stufe: Reason + Checkbox + Confirm.
  // Default-Pfad: Scanner. Manueller Pfad nur als Ausnahme (Persönliche Übergabe etc.).
  const [shipChoice, setShipChoice]               = useState(null); // Order-Obj
  const [manualShip, setManualShip]               = useState(null); // Order-Obj
  const [helperSuccessBanner, setHelperSuccessBanner] = useState(null); // { id, bestellnummer } für Erfolgsbanner mit Undo
  const [confirmUnretoure, setConfirmUnretoure] = useState(null); // order id pending revert from Retoure
  const [collapsedMonths, setCollapsedMonths]   = useState({}); // { "2026-3": true } means April 2026 collapsed
  const [archive, setArchive]                   = useState([]);
  // Helfer-Modus: manueller Toggle für Owner/Admin (Vertretungs-Modus)
  const [helperModeManual, setHelperModeManual] = useState(false);
  // Cloudflare Access Identity: wer ist eingeloggt?
  const [showArchive, setShowArchive]           = useState(false);
  const [archiveDragY, setArchiveDragY]         = useState(0);
  const [statsDragY, setStatsDragY]             = useState(0);
  const [confirmEmptyArchive, setConfirmEmptyArchive] = useState(false);
  const [lastKanal, setLastKanal]         = useState("etsy");
  const [loaded, setLoaded]               = useState(false);
  const [listVisible, setListVisible]     = useState(true);
  const [slideDir, setSlideDir]           = useState(0);

  const searchRef    = useRef(null);
  const inputRef     = useRef(null);
  const listRef      = useRef(null);
  // Inline-Label-Upload (von Offen-Pille direkt, ohne Detail-Modal-Umweg).
  // Single hidden input + Ref auf die Ziel-Order — wird bei jedem Klick neu gesetzt.
  const labelUploadInputRef = useRef(null);
  const pendingLabelOrderIdRef = useRef(null);
  const triggerLabelUpload = (orderId) => {
    pendingLabelOrderIdRef.current = orderId;
    labelUploadInputRef.current?.click();
  };
  const tabIndexRef  = useRef(0);
  const touchStartX  = useRef(null);
  const switchingRef = useRef(false);
  const switchTabRef = useRef(null);
  const saveTimerRef = useRef(null);
  const settingsStartY = useRef(null);
  // addStartY weggefallen mit Swipe-to-close (siehe addDragY-Notiz oben).
  const lagerStartY = useRef(null);
  const archiveStartY = useRef(null);
  const statsStartY = useRef(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error

  // ── State-Reset-Helpers ───────────────────────────────────
  // resetStoreState: leert alle store-spezifischen States.
  // Aufgerufen bei: Store-Wechsel, Store-Löschen, Logout.
  const resetStoreState = () => {
    setOrders([]);
    setBatches([]);
    setArchive([]);
    setMembersList([]);
    setInvitationsList([]);
    setCurrentView("dashboard");
    setShowSheet(false);
    setShowDetail(false);
    setDetailOrder(null);
    setForm(emptyForm());
    setFilterKanal("Alle");
    setSearch("");
    setLoaded(false);
    setShowArchive(false);
    setShowSettings(false);
    setShowLager(false);
    setShowStats(false);
    setShowCatalog(false);
    setLagerProductId(null);
    setAuditEntries([]);
    setShowAuditLog(false);
    setHelperModeManual(false);
    setStoreDashboardLayout(null);
    setCreatedInviteCode(null);
    setConfirmDeleteStoreOpen(false);
    setTabIndex(1);
    setGoal(100);
    setCollapsedBatches(new Set());
    setCollapsedMonths({});
    setScannerState("idle");
    setLastMatchedOrder(null);
    setChannels([]);
    setStoreStatusLabels(DEFAULT_STATUS_LABELS);
  };
  async function destroyCurrentStore() {
    if (!currentStore || !authToken) return;
    try {
      const res = await fetch(`/api/stores/${currentStore.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        showToast(d.reason || "Löschen fehlgeschlagen", "error");
        return;
      }
      showToast("Store gelöscht");
      onStoreDeleted(currentStore.id);
    } catch (e) {
      showToast("Netzwerkfehler", "error");
    }
  }

  // Helfer verlässt den Bereich (Selbst-Entfernen aus der Mitgliedschaft).
  // Backend erlaubt DELETE der eigenen Membership für jede Rolle.
  async function leaveCurrentStore() {
    if (!currentStore || !authToken || !authUser?.id) return;
    try {
      const res = await fetch(`/api/stores/${currentStore.id}/members/${authUser.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        showToast(d.reason || "Verlassen fehlgeschlagen", "error");
        return;
      }
      showToast("Bereich verlassen");
      onStoreDeleted(currentStore.id);
    } catch (e) {
      showToast("Netzwerkfehler", "error");
    }
  }

  // ── Initialize store data on mount / store change ────────
  useEffect(() => {
    if (!currentStore?.id || !authToken) return;
    loadStoreData(currentStore.id, authToken, currentRole);
    loadMyDashboardLayout(currentStore.id, authToken, currentRole);
  // eslint-disable-next-line
  }, [currentStore?.id]);

  // Load own membership row (incl. dashboard_layout). Falls back to role default.
  async function loadMyDashboardLayout(storeId, token, role) {
    try {
      const res = await fetch(`/api/stores/${storeId}/me`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) {
        setStoreDashboardLayout(defaultLayoutForRole(role));
        return;
      }
      const me = await res.json();
      if (me.dashboard_layout && Array.isArray(me.dashboard_layout.widgets)) {
        setStoreDashboardLayout(me.dashboard_layout);
      } else {
        setStoreDashboardLayout(defaultLayoutForRole(role));
      }
    } catch {
      setStoreDashboardLayout(defaultLayoutForRole(role));
    }
  }

  async function saveDashboardLayout(layout) {
    if (!currentStore || !authToken) return;
    setStoreDashboardLayout(layout);
    try {
      await fetch(`/api/stores/${currentStore.id}/me/dashboard_layout`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
        body: JSON.stringify(layout),
      });
    } catch (e) { console.warn("save layout failed:", e); }
  }

  // ── Orders: DB ↔ Frontend field conversion ────────────────
  function orderFromDb(o) {
    if (!o) return null;
    return {
      id: o.id,
      bestellnummer: o.bestellnummer || "",
      kanal: o.kanal || "",
      status: o.status || "Offen",
      datum: o.datum || (o.created_at ? o.created_at.slice(0, 10) : ""),
      // preis = Umsatz der Bestellung (Σ menge × unit_price der order_items, Snapshot).
      // Ersetzt die alte feste Preisspalte/27,99-Logik.
      preis: Array.isArray(o.order_items)
        ? o.order_items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0)
        : (parseFloat(o.price) || 0),
      notiz: o.notiz || "",
      trackingNumber: o.tracking_number || "",
      labelKey: o.label_key || "",
      labelMime: o.label_mime || "",
      retoureStatus: o.retoure_status || "",
      retoureErhalten: o.retoure_erhalten || false,
      batchId: o.batch_id || null,
      adresse: o.adresse || "",
      archived: o.archived || false,
      archivedAt: o.archived_at || null,
      createdAt: o.created_at || null,
      updatedAt: o.updated_at || null,
      // Online-Store Umbau Phase 1: Kanalbezug + Snapshots + Bestellzeilen.
      channelId: o.channel_id || null,
      feePercentSnapshot:   o.fee_percent_snapshot   != null ? parseFloat(o.fee_percent_snapshot)   : 0,
      shippingCostSnapshot: o.shipping_cost_snapshot != null ? parseFloat(o.shipping_cost_snapshot) : 0,
      orderItems: Array.isArray(o.order_items) ? o.order_items.map(it => ({
        id: it.id,
        productId: it.product_id,
        quantity: it.quantity || 0,
        unitPrice: parseFloat(it.unit_price) || 0,
        unitCost:  parseFloat(it.unit_cost)  || 0,
      })) : [],
    };
  }
  function orderToDb(o) {
    const out = {};
    const map = {
      bestellnummer: "bestellnummer", kanal: "kanal", status: "status",
      datum: "datum", notiz: "notiz", trackingNumber: "tracking_number",
      labelKey: "label_key", labelMime: "label_mime",
      retoureStatus: "retoure_status", retoureErhalten: "retoure_erhalten",
      batchId: "batch_id", adresse: "adresse",
    };
    for (const [front, db] of Object.entries(map)) {
      if (front in o) out[db] = o[front];
    }
    return out;
  }
  function batchFromDb(b) {
    return {
      id: b.id,
      name: b.name,
      stueckzahl: b.stueckzahl || 0,
      investment: parseFloat(b.investment) || 0,
      cogs: parseFloat(b.cogs) || 0,
      reservierungen: b.reservierungen || {},
      minThresholds: b.min_thresholds || {},
      createdAt: b.created_at || null,
      // Phase 2: Charge ↔ Produkt + FIFO-Restbestand
      productId: b.product_id || null,
      remaining: b.remaining != null ? Number(b.remaining) : null,
      // Wareneingang (Migration 026): Eingangsdatum (Anzeige) + Lieferungs-Gruppe
      receivedAt: b.received_at || null,
      deliveryId: b.delivery_id || null,
    };
  }

  // Produkt-Bestand = Summe der FIFO-Restmengen (remaining) aller Batches dieses
  // Produkts. remaining ist server-autoritativ; Legacy-Batches ohne remaining zählen
  // ihren vollen stueckzahl-Wert (nur Altbestände vor FIFO).
  function getProductStock(productId) {
    return (batches || [])
      .filter(b => b.productId === productId)
      .reduce((sum, b) => sum + (b.remaining != null ? b.remaining : (b.stueckzahl || 0)), 0);
  }

  // Client-Vorschau der FIFO-Stückkosten — spiegelt consumeBatchesFIFO (Worker) EXAKT,
  // damit der Vorschau-Gewinn dem gespeicherten Snapshot (order_items.unit_cost) entspricht.
  // flat → costFlat; batch → Batches created_at ASC, perUnit = cogs || investment/stueckzahl || 0,
  // take = min(remaining, need). Fehlmenge (shortfall) kostet 0 (nicht raten). Verändert KEINEN State.
  function getProductCostPreview(productId, quantity) {
    const p = (products || []).find(x => x.id === productId);
    const qty = Number(quantity) || 0;
    if (!p || qty <= 0) return { totalCost: 0, unitCost: 0, shortfall: 0 };
    if (p.costMode !== 'batch') {
      const unit = Number(p.costFlat) || 0;
      return { totalCost: unit * qty, unitCost: unit, shortfall: 0 };
    }
    const bs = (batches || [])
      .filter(b => b.productId === productId)
      .slice()
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    let need = qty, totalCost = 0;
    for (const b of bs) {
      if (need <= 0) break;
      const remaining = Number(b.remaining) || 0; // wie Server: null/leer = 0, kein stueckzahl-Fallback
      if (remaining <= 0) continue;
      const take = Math.min(remaining, need);
      let perUnit = Number(b.cogs) || 0;
      if (!perUnit && (b.stueckzahl || 0) > 0) perUnit = (Number(b.investment) || 0) / b.stueckzahl;
      totalCost += take * perUnit;
      need -= take;
    }
    return { totalCost, unitCost: qty > 0 ? totalCost / qty : 0, shortfall: Math.max(0, need) };
  }

  // ── Stock + Alarm helpers (consistent across Lager / Stats / Banner) ────
  // Total batch stock: stueckzahl - (verkauft, nicht Retoure, nicht archiviert)
  function getBatchStock(batch) {
    const used = orders.filter(o =>
      o.batchId === batch.id &&
      o.status !== "Retoure" &&
      !o.archived
    ).length;
    return Math.max(0, (batch.stueckzahl || 0) - used);
  }
  // Per-channel pool stock: reservierungen[channel] - sold[batch][channel]
  function getBatchChannelStock(batch, channelId) {
    const reserved = batch.reservierungen?.[channelId] || 0;
    const sold = orders.filter(o =>
      o.batchId === batch.id &&
      o.kanal === channelId &&
      o.status !== "Retoure" &&
      !o.archived
    ).length;
    return Math.max(0, reserved - sold);
  }
  // Alarm level for a (batch, channel) combo: null | "low" | "out"
  function channelAlarmLevel(batch, channelId) {
    const threshold = parseInt(batch.minThresholds?.[channelId], 10) || 0;
    if (threshold <= 0) return null;
    const stock = getBatchChannelStock(batch, channelId);
    if (stock <= 0) return "out";
    if (stock <= threshold) return "low";
    return null;
  }
  // Aggregate count of active alarms across all (batch, channel) combos
  function getActiveAlarms() {
    const out = [];
    for (const b of (batches || [])) {
      for (const channelId of Object.keys(b.reservierungen || {})) {
        const level = channelAlarmLevel(b, channelId);
        if (level) out.push({ batch: b, channelId, level, stock: getBatchChannelStock(b, channelId), threshold: b.minThresholds?.[channelId] || 0 });
      }
    }
    return out;
  }

  // ── Activity Log: API + describer ───────────────────────────
  async function loadAuditLog(reset = true) {
    if (!currentStore || !authToken) return;
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter.entity_type && auditFilter.entity_type !== "all") {
        params.set("entity_type", auditFilter.entity_type);
      }
      if (auditFilter.days && auditFilter.days > 0) {
        const from = new Date(Date.now() - auditFilter.days * 86400 * 1000);
        params.set("from", from.toISOString());
      }
      params.set("limit", "50");
      if (!reset && auditEntries.length > 0) {
        params.set("before", auditEntries[auditEntries.length - 1].created_at);
      }
      const res = await fetch(`/api/stores/${currentStore.id}/audit?${params}`, {
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) { setAuditLoading(false); return; }
      const data = await res.json();
      setAuditEntries(reset ? (data.entries || []) : [...auditEntries, ...(data.entries || [])]);
      setAuditHasMore(!!data.has_more);
    } catch (e) {
      console.error("loadAuditLog failed:", e);
    }
    setAuditLoading(false);
  }

  // Human-readable summary for an audit log entry
  function describeAuditEntry(e) {
    const old = e.old_value || {};
    const nv  = e.new_value || {};
    switch (e.action) {
      case "order.created":
        return { icon: "📦", text: `Bestellung #${nv.bestellnummer || "—"} erstellt` };
      case "order.updated": {
        // Compute meaningful diff
        const changes = [];
        if (old.status !== nv.status && old.status && nv.status) changes.push(`Status ${getStatusLabel(old.status)} → ${getStatusLabel(nv.status)}`);
        if (old.tracking_number !== nv.tracking_number && nv.tracking_number) changes.push(`Trackingnr ${nv.tracking_number}`);
        if (old.notiz !== nv.notiz) changes.push("Notiz geändert");
        if (old.channel_id !== nv.channel_id && nv.channel_id) changes.push(`Kanal: ${getChannelLabel(nv.channel_id) || nv.kanal || ""}`);
        const txt = changes.length ? changes.join(" · ") : "bearbeitet";
        return { icon: "📝", text: `#${nv.bestellnummer || old.bestellnummer || "—"}: ${txt}` };
      }
      case "order.archived":  return { icon: "🗑", text: "Bestellung archiviert" };
      case "order.restored":  return { icon: "↩️", text: "Bestellung wiederhergestellt" };
      case "order.deleted":   return { icon: "❌", text: "Bestellung endgültig gelöscht" };
      case "archive.emptied": return { icon: "🗑", text: `Archiv geleert (${nv?.count ?? "?"})` };
      case "batch.created":   return { icon: "📋", text: `Batch "${nv.name || "—"}" angelegt` };
      case "batch.updated":   return { icon: "📋", text: `Batch "${nv.name || old.name || "—"}" bearbeitet` };
      case "batch.deleted":   return { icon: "📋", text: `Batch "${old.name || "—"}" gelöscht` };
      case "member.role_changed": return { icon: "👥", text: `Rolle geändert auf ${nv.role || "?"}` };
      case "member.removed":  return { icon: "👥", text: "Mitglied entfernt" };
      case "invitation.created": return { icon: "✉️", text: `Einladung als ${nv.role || "?"} erstellt (${nv.code || ""})` };
      case "settings.updated": return { icon: "⚙️", text: "Einstellungen geändert" };
      case "store.config_changed": return { icon: "⚙️", text: "Store-Konfiguration geändert" };
      default: return { icon: "•", text: e.action };
    }
  }

  // Format timestamp as "vor N Min/Std/Tagen" or absolute date
  function formatRelative(iso) {
    if (!iso) return "";
    const dt = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - dt);
    if (diff < 60_000) return "gerade eben";
    if (diff < 3600_000) return `vor ${Math.floor(diff / 60_000)} Min`;
    if (diff < 86400_000) return `vor ${Math.floor(diff / 3600_000)} Std`;
    if (diff < 7 * 86400_000) return `vor ${Math.floor(diff / 86400_000)} Tagen`;
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  }

  // ── API mutation helpers (call after local optimistic update) ────
  // Online-Store Umbau Phase 1: neuer Body = channel_id + items[{product_id,quantity}].
  // unit_price/unit_cost werden serverseitig aufgelöst + gesnapshottet.
  async function apiOrderCreate(formObj) {
    if (!currentStore || !authToken) return null;
    const items = (formObj.items || [])
      .filter(it => it.productId && (Number(it.quantity) || 0) > 0)
      .map(it => ({ product_id: it.productId, quantity: Number(it.quantity) || 1 }));
    const body = {
      channel_id:    formObj.channelId || null,
      items,
      bestellnummer: formObj.bestellnummer || null,
      status:        formObj.status || "Offen",
      datum:         formObj.datum || undefined,
      notiz:         formObj.notiz || null,
      adresse:       formObj.adresse || null,
    };
    const res = await fetch(`/api/stores/${currentStore.id}/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) { showToast("Anlegen fehlgeschlagen", "error"); return null; }
    const data = await res.json();
    const order = orderFromDb(data);
    if (order && Array.isArray(data.warnings)) order.warnings = data.warnings;
    return order;
  }
  async function apiOrderUpdate(id, patch) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/orders/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(orderToDb(patch)),
    });
    if (!res.ok) { showToast("Speichern fehlgeschlagen", "error"); return null; }
    return orderFromDb(await res.json());
  }
  async function apiOrderArchive(id) {
    if (!currentStore || !authToken) return false;
    const res = await fetch(`/api/stores/${currentStore.id}/orders/${id}/archive`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (!res.ok) { showToast("Archivieren fehlgeschlagen", "error"); return false; }
    return true;
  }
  async function apiOrderRestore(id) {
    if (!currentStore || !authToken) return false;
    const res = await fetch(`/api/stores/${currentStore.id}/orders/${id}/restore`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (!res.ok) { showToast("Wiederherstellen fehlgeschlagen", "error"); return false; }
    return true;
  }
  async function apiOrderEmptyArchive() {
    if (!currentStore || !authToken) return false;
    const res = await fetch(`/api/stores/${currentStore.id}/archive`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    return res.ok;
  }
  async function apiBatchCreate(batchData) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/batches`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(batchData),
    });
    if (!res.ok) { showToast("Batch-Anlegen fehlgeschlagen", "error"); return null; }
    return batchFromDb(await res.json());
  }
  async function apiBatchUpdate(id, patch) {
    if (!currentStore || !authToken) return null;
    // Translate frontend camelCase → DB snake_case for known fields
    const dbPatch = { ...patch };
    if ("minThresholds" in dbPatch) {
      dbPatch.min_thresholds = dbPatch.minThresholds;
      delete dbPatch.minThresholds;
    }
    const res = await fetch(`/api/stores/${currentStore.id}/batches/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(dbPatch),
    });
    if (!res.ok) { showToast("Batch-Speichern fehlgeschlagen", "error"); return null; }
    return batchFromDb(await res.json());
  }
  // Wareneingang: 1 Lieferung + n Produkt-Batches in EINEM Call. Server liefert
  // { delivery, batches } zurück; Caller hängt die Batches an store.batches an.
  // payload = { received_at?, note?, positions: [{ product_id, quantity, unit_cost }] }
  async function apiDeliveryCreate(payload) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/deliveries`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) { showToast("Wareneingang fehlgeschlagen", "error"); return null; }
    const data = await res.json();
    return {
      delivery: data.delivery || null,
      batches: (data.batches || []).map(batchFromDb),
    };
  }

  // ── Produkte + Kanäle (Online-Store Umbau Phase 1) ──
  function productFromDb(p) {
    return {
      id: p.id,
      name: p.name || "",
      price: parseFloat(p.price) || 0,
      costMode: p.cost_mode === "batch" ? "batch" : "flat",
      costFlat: p.cost_flat != null ? parseFloat(p.cost_flat) : null,
      archivedAt: p.archived_at || null,
    };
  }
  function channelFromDb(c) {
    return {
      id: c.id,
      name: c.name || "",
      feePercent: parseFloat(c.fee_percent) || 0,
      shippingCost: parseFloat(c.shipping_cost) || 0,
    };
  }
  async function apiProductCreate(data) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/products`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) { showToast("Produkt-Anlegen fehlgeschlagen", "error"); return null; }
    return productFromDb(await res.json());
  }
  async function apiChannelCreate(data) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/channels`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) { showToast("Kanal-Anlegen fehlgeschlagen", "error"); return null; }
    const ch = channelFromDb(await res.json());
    setChannels(prev => prev.some(c => c.id === ch.id) ? prev : [...prev, ch]);
    return ch;
  }
  // ── Phase 2: Produkt-Update / -Archiv, Kanal-Update, Pro-Kanal-Override ──
  // Patches werden snake_case erwartet (wie apiProductCreate `data`).
  async function apiProductUpdate(id, patch) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/products/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(patch),
    });
    if (!res.ok) { showToast("Produkt-Speichern fehlgeschlagen", "error"); return null; }
    return productFromDb(await res.json());
  }
  // Soft-Archive via archived_at (null = wiederherstellen). FK RESTRICT auf
  // order_items → niemals hart löschen.
  function apiProductArchive(id, archive = true) {
    return apiProductUpdate(id, { archived_at: archive ? new Date().toISOString() : null });
  }
  async function apiChannelUpdate(id, patch) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/channels/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(patch),
    });
    if (!res.ok) { showToast("Kanal-Speichern fehlgeschlagen", "error"); return null; }
    const ch = channelFromDb(await res.json());
    setChannels(prev => prev.map(c => c.id === ch.id ? ch : c));
    return ch;
  }
  // Optionaler Pro-Kanal-Preis: REPLACE-Semantik — `prices` ist die VOLLSTÄNDIGE
  // Override-Liste des Produkts ([{ channel_id, price }]). Leeres Array löscht alle.
  async function apiSetProductChannelPrices(productId, prices) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/products/${productId}/channel-prices`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify({ prices }),
    });
    if (!res.ok) { showToast("Preise-Speichern fehlgeschlagen", "error"); return null; }
    return await res.json();
  }
  // Bestehende Overrides eines Produkts laden (lazy beim Sheet-Öffnen).
  async function apiGetProductChannelPrices(productId) {
    if (!currentStore || !authToken) return [];
    const res = await fetch(`/api/stores/${currentStore.id}/products/${productId}/channel-prices`, {
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (!res.ok) return [];
    const rows = await res.json();
    return (Array.isArray(rows) ? rows : []).map(r => ({
      channelId: r.channel_id,
      price: parseFloat(r.price) || 0,
    }));
  }
  // Stellt sicher, dass der Store ≥1 Produkt + ≥1 Kanal hat, sonst bricht der
  // Bestell-Flow. Wird einmalig nach dem Laden ausgeführt (seededRef-Guard).
  // Nur owner/admin dürfen Katalog schreiben → Helfer-Stores ohne Defaults
  // würden hier nichts anlegen (akzeptiert: Helfer legen ohnehin auf bestehenden
  // Katalog an).
  async function ensureStoreDefaults(loadedProducts, loadedChannels) {
    if (seededRef.current) return { products: loadedProducts, channels: loadedChannels };
    if (!can("manage_catalog")) return { products: loadedProducts, channels: loadedChannels };
    seededRef.current = true;
    let prods = loadedProducts, chans = loadedChannels;
    if (chans.length === 0) {
      const ch = await apiChannelCreate({ name: "Direktverkauf", fee_percent: 0, shipping_cost: 0 });
      if (ch) { chans = [ch]; setChannels(chans); }
    }
    if (prods.length === 0) {
      const p = await apiProductCreate({ name: "Standardprodukt", price: 27.99, cost_mode: "flat", cost_flat: 0 });
      if (p) { prods = [p]; setProducts(prods); }
    }
    return { products: prods, channels: chans };
  }

  async function loadStoreData(storeId, token, role) {
    // DEV-only seed injection — tree-shaken in production (import.meta.env.DEV === false)
    if (import.meta.env.DEV) {
      if (getLoadingOverride()) {
        return; // Stay in loading state permanently
      }
      const seedOrders = getSeedOrders();
      if (seedOrders !== null) {
        setOrders(seedOrders);
        setLoaded(true);
        return;
      }
    }
    try {
      const headers = { "Authorization": `Bearer ${token}` };
      const [ordersRes, archivedRes, settingsRes, batchesRes, productsRes, channelsRes] = await Promise.all([
        fetch(`/api/stores/${storeId}/orders`, { headers }),
        fetch(`/api/stores/${storeId}/orders?archived=true`, { headers }),
        fetch(`/api/stores/${storeId}/settings`, { headers }),
        fetch(`/api/stores/${storeId}/batches`, { headers }),
        fetch(`/api/stores/${storeId}/products`, { headers }),
        fetch(`/api/stores/${storeId}/channels`, { headers }),
      ]);
      // 403 = User ist nicht mehr Mitglied dieses Stores → clean reset zur Landing
      if (ordersRes.status === 403 || settingsRes.status === 403) {
        showToast("Kein Zugriff auf diesen Bereich", "warning");
        onBack();
        return;
      }
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        const arr = Array.isArray(data) ? data : (data.orders || []);
        setOrders(arr.map(orderFromDb));
        setHasMoreOrders(!!data.has_more);
        setOrdersCursor(data.next_cursor || null);
      }
      if (archivedRes.ok) {
        const data = await archivedRes.json();
        const arr = Array.isArray(data) ? data : (data.orders || []);
        setArchive(arr.map(orderFromDb));
      }
      if (settingsRes.ok) {
        const s = await settingsRes.json();
        if (s.filter_kanal) setFilterKanal(s.filter_kanal);
        if (s.last_kanal) setLastKanal(s.last_kanal);
        if (typeof s.goal === "number") setGoal(s.goal);
        // s.batch_investment ist legacy — wird ignoriert. Investment kommt nur per-Batch.
      }
      if (batchesRes.ok) {
        const batches = await batchesRes.json();
        if (Array.isArray(batches) && batches.length > 0) {
          setBatches(batches.map(batchFromDb));
        }
      }
      // Online-Store Umbau Phase 1: Produkte + Kanäle laden, dann Default-Seed
      // (≥1 Produkt + ≥1 Kanal), damit der Bestell-Flow nie ohne Katalog dasteht.
      let loadedProducts = [], loadedChannels = [];
      if (productsRes.ok) {
        const arr = await productsRes.json();
        loadedProducts = (Array.isArray(arr) ? arr : []).map(productFromDb);
        setProducts(loadedProducts);
      }
      if (channelsRes.ok) {
        const arr = await channelsRes.json();
        loadedChannels = (Array.isArray(arr) ? arr : []).map(channelFromDb);
        setChannels(loadedChannels);
      }
      if (productsRes.ok && channelsRes.ok) {
        await ensureStoreDefaults(loadedProducts, loadedChannels);
      }
      setLoaded(true);
      // Audit-Log im Hintergrund laden — damit das Aktivitäts-Widget auf dem
      // Dashboard sofort Daten hat (vorher musste der User erst das Audit-Modal
      // öffnen damit Daten kamen). Nur Owner/Admin (visibleFor des Widgets).
      if (role === "owner" || role === "admin") {
        // Audit-Filter auf "alle Entitäten, letzte 7 Tage" voreinstellen
        setAuditFilter({ entity_type: "all", days: 7 });
        setAuditEntries([]);
        // Defer mit setTimeout damit alle State-Updates oben durch sind
        setTimeout(() => loadAuditLog(true), 0);
      }
    } catch (e) {
      console.error("loadStoreData failed:", e);
      setLoaded(true);
    }
  }

  async function loadMoreOrders() {
    if (!currentStore || !authToken || !ordersCursor) return;
    try {
      const res = await fetch(
        `/api/stores/${currentStore.id}/orders?before=${encodeURIComponent(ordersCursor)}`,
        { headers: { "Authorization": `Bearer ${authToken}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data.orders || []);
      setOrders(prev => [...prev, ...arr.map(orderFromDb)]);
      setHasMoreOrders(!!data.has_more);
      setOrdersCursor(data.next_cursor || null);
    } catch (e) {
      console.warn("loadMoreOrders failed:", e);
    }
  }

  // Debounced settings save — fires when settings-style state changes
  useEffect(() => {
    if (!loaded || !currentStore || !authToken) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState("saving");
    saveTimerRef.current = setTimeout(async () => {
      try {
        const settingsRes = await fetch(`/api/stores/${currentStore.id}/settings`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({
            last_kanal: lastKanal, filter_kanal: filterKanal,
            goal,
          }),
        });
        if (settingsRes.ok) {
          setSaveState("saved");
          setTimeout(() => setSaveState("idle"), 1500);
        } else {
          setSaveState("error");
        }
      } catch { setSaveState("error"); }
    }, 500);
    // NOTE: orders/batches/archive are NOT in deps — they sync via per-mutation API calls.
    // Only settings-style values trigger this debounced save.
  }, [lastKanal, filterKanal, goal, loaded]);

  // Lokale Settings (dark + sortBy) bei Änderung sofort in localStorage persistieren
  useEffect(() => {
    try { localStorage.setItem("memorica_dark", dark ? "1" : "0"); } catch {}
  }, [dark]);
  useEffect(() => {
    try { localStorage.setItem("memorica_sortBy", sortBy); } catch {}
  }, [sortBy]);
  useEffect(() => {
    try { localStorage.setItem("memorica_dateRange", String(dateRange)); } catch {}
  }, [dateRange]);

  useEffect(() => {
    // Only lock scroll on mobile/tablet where sheets cover the viewport.
    // On desktop, panels are inline in the right column — body should stay scrollable.
    const locked = !isDesktop && (showSheet || showDetail);
    document.body.style.overflow = locked ? "hidden" : "";
    document.documentElement.style.overflow = locked ? "hidden" : "";
    return () => { document.body.style.overflow = ""; document.documentElement.style.overflow = ""; };
  }, [showSheet, showDetail, isDesktop]);

  useEffect(() => {
    const light = document.getElementById("theme-color");
    const darkTag = document.getElementById("theme-color-dark");
    const color = dark ? "#0d1117" : "#dde8f8";
    if (light) light.setAttribute("content", color);
    if (darkTag) darkTag.setAttribute("content", color);
    document.body.classList.toggle("light-mode", !dark);
    document.body.style.background = color;
    document.documentElement.style.background = color;
  }, [dark]);

  useEffect(() => { tabIndexRef.current = tabIndex; }, [tabIndex]);

  // Helfer-Modus: fulfillment-Mitglieder sind IMMER im Helfer-Modus (kein Toggle).
  // Owner/Admin können den Modus manuell aktivieren (z.B. für Vertretung).
  const helperMode = helperModeManual || currentRole === 'fulfillment';

  const toggleHelperMode = useCallback(() => {
    if (currentRole === 'fulfillment') return; // fulfillment kann nicht toggling
    setHelperModeManual(v => !v);
  }, [currentRole]);

  const helperModeRef = useRef(false);
  useEffect(() => { helperModeRef.current = helperMode; }, [helperMode]);
  useEffect(() => { if (showSearch) searchRef.current?.focus(); else setSearch(""); }, [showSearch]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const onEnd   = (e) => {
      if (touchStartX.current === null) return;
      const delta = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(delta) < 48) return;
      if (helperModeRef.current) return; // Helfer-Modus: nur Offen-Tab, kein Swipe
      const cur = tabIndexRef.current;
      if (delta < 0 && cur < TABS.length - 1) switchTabRef.current(cur + 1);
      if (delta > 0 && cur > 0) switchTabRef.current(cur - 1);
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend",   onEnd,   { passive: true });
    return () => { el.removeEventListener("touchstart", onStart); el.removeEventListener("touchend", onEnd); };
  }, []);

  const switchTab = (newIdx) => {
    if (newIdx === tabIndexRef.current || switchingRef.current) return;
    switchingRef.current = true;
    const dir = newIdx > tabIndexRef.current ? -1 : 1;
    setSlideDir(dir); setListVisible(false);
    setTimeout(() => {
      setTabIndex(newIdx); setSlideDir(-dir);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setSlideDir(0); setListVisible(true); switchingRef.current = false;
      }));
    }, 150);
  };
  switchTabRef.current = switchTab;

  const t = dark ? T.dark : T.light;

  const glass = (extra = {}) => ({
    background: t.glass,
    backdropFilter: "blur(40px) saturate(200%)",
    WebkitBackdropFilter: "blur(40px) saturate(200%)",
    border: `1px solid ${t.glassBorder}`,
    boxShadow: t.glassShadow,
    ...extra,
  });

  const inp = (s = {}) => ({
    width: "100%", border: "none", background: "transparent",
    fontFamily: F, fontSize: 17, color: t.text, outline: "none",
    padding: "16px 18px", boxSizing: "border-box", lineHeight: 1.3, ...s
  });

  // showToast delegates to the global toast system
  const showToast = (msg = "Sendungsnummer kopiert", type = "success") => {
    if (type === "error") _toast.error(msg);
    else if (type === "warning") _toast.warning(msg);
    else if (type === "info") _toast.info(msg);
    else _toast.success(msg);
  };
  const openNew = () => {
    // CRITICAL für iOS-Tastatur: flushSync zwingt React den Sheet SYNCHRON zu rendern,
    // BEVOR wir aus dem Click-Handler raus sind. Der Input ist dann mounted und wir
    // sind noch im user-gesture-Context → focus() öffnet die Tastatur zuverlässig.
    // Vorher: setTimeout-Retries, die nach Gesture-Context kamen → iOS verweigerte Tastatur.
    // Phase 1: Default-Kanal (zuletzt genutzter, sonst erster) + eine Produktzeile
    // mit dem ersten nicht-archivierten Produkt vorbelegen.
    const chId = channels.find(c => c.id === lastKanal)?.id || channels[0]?.id || null;
    const firstProduct = products.find(p => !p.archivedAt) || products[0];
    const initItems = firstProduct ? [{ productId: firstProduct.id, quantity: 1 }] : [];
    flushSync(() => {
      setForm({ ...emptyForm(chId), items: initItems });
      setShowSheet(true);
    });
    inputRef.current?.focus({ preventScroll: true });
    // Defensiv: zusätzliche Refocus-Versuche falls die erste Iteration aus
    // irgendwelchen Gründen scheitert (Animation, Layout-Shift, etc.).
    const refocus = () => inputRef.current?.focus({ preventScroll: true });
    refocus();
    requestAnimationFrame(refocus);
    setTimeout(refocus, 80);
    setTimeout(refocus, 200);
  };
  const closeSheet  = () => { setShowSheet(false); setForm(emptyForm(lastKanal)); };
  const isDuplicate = form.bestellnummer && orders.some(o => o.bestellnummer.trim() === form.bestellnummer.trim());

  const save = async () => {
    if (!form.bestellnummer || isDuplicate) return;
    // Online-Store Umbau Phase 1: KEINE Batch-Pflicht mehr. Stattdessen: Kanal +
    // ≥1 Produktzeile erforderlich. Kosten kommen aus cost_mode (flat/batch).
    const items = (form.items || []).filter(it => it.productId && (Number(it.quantity) || 0) > 0);
    if (items.length === 0) {
      showToast("Mindestens ein Produkt wählen", "warning");
      return;
    }
    if (!form.channelId) {
      showToast("Bitte einen Kanal wählen", "warning");
      return;
    }
    setLastKanal(form.channelId);
    haptic(12);
    // Snapshot der zu submittenden Daten BEVOR wir das Form zurücksetzen.
    const formSnapshot = { ...form };
    // Kanal + Produktzeilen für schnelle Mehrfacherfassung behalten, Bestellnummer leeren.
    setForm(f => ({ ...emptyForm(f.channelId), items: f.items }));
    // CRITICAL: synchroner Refocus VOR dem await — sonst bricht iOS den
    // user-gesture-Context auf und die Tastatur lässt sich nicht wieder öffnen.
    inputRef.current?.focus({ preventScroll: true });
    // Async API-Call läuft im Hintergrund — UI bleibt responsiv, Input behält Fokus.
    const created = await apiOrderCreate(formSnapshot);
    if (created) {
      setOrders(o => [created, ...o]);
      if (created.warnings?.length) showToast(created.warnings[0], "warning");
    }
    // Neue Bestellungen sind "Offen" und nur im Alle-Tab sichtbar.
    if (filterStatus !== "Alle") setTabIndex(0);
    if (listRef.current) listRef.current.scrollTop = 0;
    // Defensive Mehrfach-Refocus nach dem await als Fallback
    const refocus = () => inputRef.current?.focus({ preventScroll: true });
    requestAnimationFrame(refocus);
    setTimeout(refocus, 50);
    setTimeout(refocus, 150);
  };
  const openDetail  = (order) => { setShowStats(false); setShowLager(false); setShowSettings(false); setShowArchive(false); setDetailOrder(order); setDetailDeleteConfirm(false); setShowDetail(true); };

  // Desktop: closes all other panels before opening the requested one (mutex)
  const openPanel = (which) => {
    setShowStats(which === "stats");
    setShowLager(which === "lager");
    setShowSettings(which === "settings");
    setShowArchive(which === "archive");
    if (which !== "detail") { setShowDetail(false); setDetailOrder(null); }
  };

  const closeDetail = () => { setShowDetail(false); setDetailOrder(null); setDetailDeleteConfirm(false); };
  const updateOrder = (id, field, val) => {
    // Optimistic local update
    let computedPatch = { [field]: val };
    setOrders(o => o.map(x => {
      if (x.id !== id) return x;
      const next = { ...x, [field]: val };
      if (field === "labelKey") {
        const hasLabel = typeof val === "string" && val.length > 0;
        if (next.status === "Offen" && hasLabel) { next.status = "Bereit"; computedPatch.status = "Bereit"; }
        else if (next.status === "Bereit" && !hasLabel) { next.status = "Offen"; computedPatch.status = "Offen"; }
      }
      return next;
    }));
    // Sync to API in background
    apiOrderUpdate(id, computedPatch);
  };
  // Label hochladen + Order updaten in einem Schritt
  const setLabelForOrder = async (id, file) => {
    try {
      const order = orders.find(x => x.id === id);
      // Altes Label aus R2 löschen falls vorhanden
      if (order?.labelKey) await deleteLabel(order.labelKey, currentStore?.id, authToken);
      const { key, mime } = await uploadLabel(file, id, currentStore?.id, authToken);
      const patch = { labelKey: key, labelMime: mime };
      // Auto-Transition Offen → Bereit (Workflow-Constraint: nur Bereit-Orders
      // werden für Tracking-Extraktion bearbeitet, der Auto-Transition macht
      // die Order direkt Bereit)
      if (order?.status === "Offen") patch.status = "Bereit";
      setOrders(o => o.map(x => x.id === id ? { ...x, ...patch } : x));
      apiOrderUpdate(id, patch);
      haptic(15);
      showToast("Label hochgeladen ✓");

      // Tracking-Nummer aus dem Label extrahieren (client-side, async).
      // Gilt nur für jetzt "Bereit"-Bestellungen. Wir starten die Extraktion
      // entkoppelt vom Upload — der UI-Flow bleibt unblockiert.
      const newStatus = patch.status || order?.status;
      if (newStatus === "Bereit") {
        showToast("Trackingnummer wird gelesen…", "info");
        try {
          const result = await extractTrackingNumber(file);
          if (!result) {
            showToast("Trackingnummer nicht erkannt — bitte manuell eingeben", "warning");
            return;
          }
          const prevTracking = order?.trackingNumber || null;

          // Helper: auto-save with rollback on API failure
          const autoSaveTracking = async (value) => {
            setOrders(o => o.map(x => x.id === id ? { ...x, trackingNumber: value } : x));
            const ok = await apiOrderUpdate(id, { trackingNumber: value });
            if (!ok) {
              setOrders(o => o.map(x => x.id === id ? { ...x, trackingNumber: prevTracking } : x));
              showToast("Trackingnummer konnte nicht gespeichert werden", "error");
              return false;
            }
            haptic(15);
            showToast(`Trackingnummer: ${value} ✓`);
            return true;
          };

          // Barcode-Quelle ist hochzuverlässig → auto-save ohne Bestätigung
          if (result.source === "barcode" && result.primary) {
            await autoSaveTracking(result.primary);
            return;
          }
          // OCR-Quelle: nur Bestätigung wenn unsicher (mehrere Kandidaten mit ähnlichem Score,
          // oder kein klarer Treffer). Eindeutiger Keyword-Treffer oder einziger Kandidat → auto-save.
          const cands = result.candidates || [];
          const topCandidate = cands[0] || null;
          const hasKeywordHit = topCandidate?.name === "keyword";
          const isGenericOcrMatch = topCandidate?.name === "alpha" || topCandidate?.name === "gls11" || topCandidate?.name === "dhl12";
          const hasClearLead = cands.length >= 2 && (cands[0].score - cands[1].score > 20);
          const isHighConfidence =
            hasKeywordHit ||
            (!!topCandidate && !isGenericOcrMatch && cands.length === 1 && topCandidate.score >= 80) ||
            (!!topCandidate && !isGenericOcrMatch && hasClearLead && topCandidate.score >= 80);

          if (result.primary && isHighConfidence) {
            await autoSaveTracking(result.primary);
            return;
          }
          // Unsicher oder kein Treffer → Bestätigung zeigen
          setTrackingConfirm({
            orderId: id,
            candidates: cands,
            ocrText: result.ocrText || "",
            manualValue: result.primary || "",
          });
          haptic(10);
        } catch (extractErr) {
          console.warn("Tracking-Extraktion fehlgeschlagen:", extractErr);
          showToast("Trackingnummer-Erkennung fehlgeschlagen", "error");
        }
      }
    } catch (e) {
      console.error("Label-Upload fehlgeschlagen:", e);
      showToast(`Label-Upload fehlgeschlagen: ${e.message}`, "error");
    }
  };
  // Label öffnen (signed URL holen, in neuem Tab öffnen)
  const openLabel = async (key) => {
    try {
      const url = await getLabelUrl(key, currentStore?.id, authToken);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error(e);
      showToast("Label kann nicht geöffnet werden: " + e.message, "error");
    }
  };
  const deleteFromDetail = async (id) => {
    const order = orders.find(x => x.id === id);
    if (order) {
      // Optimistic move to archive
      setArchive(a => [{ ...order, archived: true, archivedAt: new Date().toISOString() }, ...a]);
      setOrders(o => o.filter(x => x.id !== id));
      const ok = await apiOrderArchive(id);
      if (!ok) {
        // Rollback
        setArchive(a => a.filter(x => x.id !== id));
        setOrders(o => [order, ...o]);
      } else {
        showToast("Bestellung archiviert");
      }
    }
    closeDetail();
  };
  // Hard-Delete einer aktiven Bestellung (owner/admin; server enforced via can(delete)).
  // COGS/Bestand sind live aus der Orders-Liste berechnet → Entfernen genügt.
  // Label vorab aus R2 löschen, damit kein Orphan zurückbleibt.
  const deleteOrder = async (id) => {
    const order = orders.find(x => x.id === id);
    if (!order) return false;
    setOrders(o => o.filter(x => x.id !== id)); // optimistisch
    haptic(10);
    if (!currentStore || !authToken) return false;
    try {
      const res = await fetch(`/api/stores/${currentStore.id}/orders/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error("delete failed");
      if (order.labelKey) deleteLabel(order.labelKey, currentStore.id, authToken);
      return true;
    } catch {
      setOrders(o => [order, ...o]); // Rollback
      showToast("Löschen fehlgeschlagen", "error");
      return false;
    }
  };
  const restoreFromArchive = async (id) => {
    const item = archive.find(x => x.id === id);
    if (!item) return;
    // Optimistic move back to active orders
    setOrders(o => [{ ...item, archived: false, archivedAt: null }, ...o]);
    setArchive(a => a.filter(x => x.id !== id));
    haptic(8);
    const ok = await apiOrderRestore(id);
    if (!ok) {
      setOrders(o => o.filter(x => x.id !== id));
      setArchive(a => [item, ...a]);
    } else {
      showToast("Wiederhergestellt");
    }
  };
  const purgeFromArchive = async (id) => {
    const item = archive.find(x => x.id === id);
    setArchive(a => a.filter(x => x.id !== id));
    haptic(10);
    // Permanent delete via DELETE on the order itself
    if (currentStore && authToken) {
      try {
        await fetch(`/api/stores/${currentStore.id}/orders/${id}`, {
          method: "DELETE",
          headers: { "Authorization": `Bearer ${authToken}` },
        });
        // Cleanup the label too
        if (item?.labelKey) deleteLabel(item.labelKey, currentStore.id, authToken);
        showToast("Endgültig gelöscht");
      } catch {
        showToast("Löschen fehlgeschlagen", "error");
      }
    }
  };
  const emptyArchive = async () => {
    const oldArchive = archive;
    setArchive([]);
    setConfirmEmptyArchive(false);
    haptic(15);
    const ok = await apiOrderEmptyArchive();
    if (!ok) {
      setArchive(oldArchive);
      showToast("Archiv konnte nicht geleert werden", "error");
    } else {
      showToast("Archiv geleert");
    }
  };
  const confirmRetoureNow = () => {
    if (!confirmRetoure) return;
    haptic(8);
    setOrders(o => o.map(x => x.id !== confirmRetoure ? x : { ...x, status: "Retoure" }));
    apiOrderUpdate(confirmRetoure, { status: "Retoure" });
    setConfirmRetoure(null);
    showToast("Als Retoure markiert");
  };
  const confirmUnretoureNow = () => {
    if (!confirmUnretoure) return;
    haptic(8);
    setOrders(o => o.map(x => x.id !== confirmUnretoure ? x : { ...x, status: "Versendet", retoureErhalten: false }));
    apiOrderUpdate(confirmUnretoure, { status: "Versendet", retoureErhalten: false });
    setConfirmUnretoure(null);
    showToast("Retoure zurückgenommen");
  };
  const toggleRetoureErhalten = (id) => {
    haptic(10);
    const order = orders.find(o => o.id === id);
    if (!order) return;
    const newVal = !order.retoureErhalten;
    setOrders(o => o.map(x => x.id !== id ? x : { ...x, retoureErhalten: newVal }));
    apiOrderUpdate(id, { retoureErhalten: newVal });
  };
  const exportCSV = () => {
    const rows = [["Bestellnummer","Kanal","Status","Datum","Sendungsnummer","Notiz"],
      ...orders.map(o=>[o.bestellnummer,o.kanal,o.status,o.datum,o.trackingNumber||"",o.notiz||""])];
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([rows.map(r=>r.map(v=>`"${v}"`).join(",")).join("\n")],{type:"text/csv"})), download:`memorica-${today()}.csv` });
    a.click();
  };

  // Im Helfer-Modus immer "Offen" — egal welcher Tab gewählt ist
  // Im Helfer-Modus zeigt die Liste NUR "Bereit"-Bestellungen.
  // "Offen" (= Etikett noch nicht da) ist Noahs Aufgabe und für Vater nicht relevant.
  const filterStatus = helperMode ? "Bereit" : TABS[tabIndex];
  // Derived from batches (single source of truth if batches exist)
  const totalStueckzahl  = batches.reduce((s, b) => s + (b.stueckzahl || 0), 0);
  const totalInvestment  = batches.reduce((s, b) => s + (b.investment || 0), 0);
  const totalReserviert  = batches.reduce((s, b) => s + Object.values(b.reservierungen || {}).reduce((a, v) => a + (v || 0), 0), 0);
  const totalUnreserved  = batches.reduce((s, b) => {
    const res = Object.values(b.reservierungen || {}).reduce((a, v) => a + (v || 0), 0);
    return s + Math.max(0, (b.stueckzahl || 0) - res);
  }, 0);
  const effGoal          = totalStueckzahl > 0 ? totalStueckzahl : goal;
  // effInvestment ist jetzt einfach die Summe aller Batch-Investments.
  // Legacy-Fallback auf den alten Globals-Wert wurde entfernt.
  const effInvestment    = totalInvestment;

  // FIFO with kanal-pool awareness:
  // A sale uses the oldest batch where the kanal-pool has remaining capacity.
  // If no pool has capacity (all overdrawn), still assigns to oldest batch — system shows red warning.
  const getBatchForOrder = (order) => {
    if (order.batchId) return batches.find(b => b.id === order.batchId) || batches[0];
    const kanal = order.kanal;
    const sortedBatches = batches.slice().sort((x, y) => (x.createdAt || "").localeCompare(y.createdAt || ""));
    // Count sales per (batch, kanal)
    const soldByBatchKanal = {};
    orders.forEach(o => {
      if (o.status === "Retoure") return;
      if (!o.batchId) return;
      const key = `${o.batchId}_${o.kanal}`;
      soldByBatchKanal[key] = (soldByBatchKanal[key] || 0) + 1;
    });
    // Try to find oldest batch with kanal pool capacity
    for (const b of sortedBatches) {
      const pool = b.reservierungen?.[kanal] || 0;
      const sold = soldByBatchKanal[`${b.id}_${kanal}`] || 0;
      if (pool - sold > 0) return b;
    }
    // None has capacity — fall back to oldest batch (overdraw)
    return sortedBatches[0] || batches[0];
  };

  // Findet den ältesten Batch mit Restkapazität (kanal-übergreifend gezählt).
  // Liefert null wenn kein Batch mehr Stück frei hat. Genutzt für:
  //   • Order-Submit-Block (kein leerer Verkauf)
  //   • Aktiv-Batch-Banner im Finanz-Panel
  //   • Lager-leer-Pille im Order-List-Header
  const getActiveBatch = () => {
    if (!batches || batches.length === 0) return null;
    const sorted = batches.slice().sort((x, y) => (x.createdAt || "").localeCompare(y.createdAt || ""));
    // Verkäufe pro Batch zählen (excl. Retouren + archivierte)
    const soldByBatch = {};
    orders.forEach(o => {
      if (o.status === "Retoure" || o.archived) return;
      if (!o.batchId) return;
      soldByBatch[o.batchId] = (soldByBatch[o.batchId] || 0) + 1;
    });
    for (const b of sorted) {
      const sold = soldByBatch[b.id] || 0;
      if (sold < (b.stueckzahl || 0)) return b;
    }
    return null; // alles ausverkauft
  };

  // Datum-Bereich: nur auf abgeschlossene Bestellungen (Versendet/Retoure) anwenden,
  // damit Offen/Bereit niemals fälschlich versteckt werden.
  const dateRangeCutoff = (() => {
    if (dateRange < 0) return null; // alles
    const d = new Date();
    d.setDate(d.getDate() - dateRange);
    return d.toISOString().slice(0, 10);
  })();
  const passesDateRange = (o) => {
    if (!dateRangeCutoff) return true;
    if (o.status !== "Versendet" && o.status !== "Retoure") return true;
    return (o.datum || "") >= dateRangeCutoff;
  };
  const filtered = orders.filter(o =>
    (filterStatus === "Alle" || o.status === filterStatus) &&
    (filterKanal  === "Alle" || o.kanal  === filterKanal) &&
    passesDateRange(o) &&
    (!search || (() => {
      const q = search.toLowerCase().trim();
      // Datum auch durchsuchbar als deutsches Format ("11.04.2026", "April")
      const months = ["januar","februar","märz","april","mai","juni","juli","august","september","oktober","november","dezember"];
      const dateStr = (() => {
        const d = new Date(o.datum); if (isNaN(d)) return o.datum || "";
        return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()} ${months[d.getMonth()]}`;
      })();
      return [o.bestellnummer, o.trackingNumber, o.notiz, o.kanal, dateStr]
        .some(field => field?.toLowerCase?.().includes(q));
    })())
  ).sort((a, b) => {
    if (filterStatus === "Bereit") {
      // Bereit-Tab: älteste zuerst (= dringendste oben)
      const dateDiff = (a.datum || "").localeCompare(b.datum || "");
      return dateDiff !== 0 ? dateDiff : a.id - b.id;
    }
    if (filterStatus === "Alle") {
      // Alle-Tab: Offen ohne Label oben (Action needed), dann Bereit, dann Rest. Innerhalb: älteste zuerst.
      const order = (s) => s === "Offen" ? 0 : s === "Bereit" ? 1 : s === "Versendet" ? 2 : 3;
      const oa = order(a.status), ob = order(b.status);
      if (oa !== ob) return oa - ob;
      const dateDiff = (a.datum || "").localeCompare(b.datum || "");
      return dateDiff !== 0 ? dateDiff : a.id - b.id;
    }
    // All others: newest first by datum, then id as tiebreaker
    const dateDiff = (b.datum || "").localeCompare(a.datum || "");
    return dateDiff !== 0 ? dateDiff : (b.id || 0) - (a.id || 0);
  });
  // Group by month
  const MONTHS_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const groupByMonth = (list) => {
    const groups = [];
    let currentKey = null;
    list.forEach(o => {
      const d = new Date(o.datum || 0);
      const key = isNaN(d.getTime()) ? "—" : `${d.getFullYear()}-${d.getMonth()}`;
      const label = isNaN(d.getTime()) ? "Ohne Datum" : `${MONTHS_DE[d.getMonth()]} ${d.getFullYear()}`;
      if (key !== currentKey) {
        groups.push({ key, label, items: [] });
        currentKey = key;
      }
      groups[groups.length - 1].items.push(o);
    });
    return groups;
  };
  const groupedFiltered = groupByMonth(filtered);
  const counts   = { Alle: orders.length, Bereit: orders.filter(o=>o.status==="Bereit").length, Versendet: orders.filter(o=>o.status==="Versendet").length, Retoure: orders.filter(o=>o.status==="Retoure").length };
  const revenue  = orders.filter(o => o.status !== "Retoure").reduce((s, o) => s + getOrderRevenue(o), 0);
  const tabColors = { Alle: t.text, Bereit: "var(--accent-troedel)", Versendet: "var(--accent-stand)", Retoure: "var(--accent-red)" };
  const pillLeft  = `calc(${tabIndex * 25}% + 3px)`;
  const pillWidth = `calc(25% - 6px)`;
  const o = showDetail ? orders.find(x => x.id === detailOrder?.id) : null;

  // On desktop, the detail view becomes inline in the right panel (no modal)
  const detailInline = isDesktop;
  // On desktop, all sheets become centered popups instead of bottom sheets
  const sheetsAsPopup = isDesktop;

  // Desktop right-column panel positioning (used by sheets that should overlay the right column)
  const dRight = `max(0px, calc((100vw - ${containerMaxW}px) / 2))`;
  // Desktop-Sheet als schwebende Karte unter dem Top-Header (32px Margin),
  // mit Rundungen und Glas-Schatten — passend zum Hauptlayout.
  // Höhe: scrollbar, mit kleinem Bottom-Margin für Atmen.
  // Panel-Top liegt bündig mit dem Listen-Card-Top auf der linken Seite
  // (= Header-Höhe ~180 + Group-Header-Höhe ~23 + minimaler Spacer).
  const desktopRightPanelMarginTop = 221;
  const desktopRightPanel = isDesktop ? {
    position: "fixed",
    top: desktopRightPanelMarginTop,
    // Panel right: 32px innen vom Wrapper-Right (= Right-Column-Right-Edge)
    right: `calc(${dRight} + 32px)`,
    // Panel width: Right-Column-Width = wrapper - 2*32 (padding) - listW - 24 (gap)
    width: `min(calc(100vw - ${listW}px - 88px), ${containerMaxW - listW - 88}px)`,
    height: `calc(100vh - ${desktopRightPanelMarginTop + 24}px)`,
    maxHeight: `calc(100vh - ${desktopRightPanelMarginTop + 24}px)`,
    borderRadius: 24,
    transform: "none",
    paddingBottom: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  } : null;
  // Centered popup positioning (used for compact sheets like Add, Sendung on desktop)
  const desktopCenterPopup = isDesktop ? {
    position: "fixed",
    top: "50%",
    left: "50%",
    bottom: "auto",
    right: "auto",
    width: "min(480px, calc(100vw - 64px))",
    maxWidth: "480px",
    transform: "translate(-50%, -50%)",
    borderRadius: 24,
    paddingBottom: 24,
    maxHeight: "85vh",
  } : null;

  // Stale-session reminder is handled at app level (app.jsx) — not here.

  // ── Onboarding screens (rendered before the main app) ────

  // ── Dashboard / View navigation helpers ────────────────
  // Called by widgets (onNavigate) to jump to specific app sections
  function handleWidgetNavigate(target) {
    haptic(8);
    switch (target) {
      case "orders_bereit":
        setCurrentView("orders"); setTabIndex(1); break;
      case "orders":
        setCurrentView("orders"); break;
      case "lager":
        setCurrentView("orders"); setTimeout(() => { openPanel?.("lager"); setShowLager(true); }, 50);
        break;
      case "stats":
        setCurrentView("orders"); setTimeout(() => { openPanel?.("stats"); setShowStats(true); }, 50);
        break;
      case "goal_edit":
        // Direkt das Goal-Edit-Popup öffnen, ohne View-Switch
        setShowGoalEdit(true);
        break;
      case "audit":
        setShowAuditLog(true);
        setAuditFilter({ entity_type: "all", days: 7 });
        setAuditEntries([]);
        setTimeout(() => loadAuditLog(true), 0);
        break;
      default:
        break;
    }
  }

  // Build the visible widget list from layout + role + visibility
  function getVisibleWidgets() {
    const layout = storeDashboardLayout || defaultLayoutForRole(currentRole);
    // widgetProps werden weiter unten beim Rendern zusammengebaut — wir bauen
    // hier nur die für `shouldRender` nötigen Felder zusammen.
    const sharedProps = { alarms: getActiveAlarms(), orders };
    // Widgets, die im Code-Default-Layout sind, aber im gespeicherten Layout fehlen
    // (z.B. später hinzugefügt wie `stale_orders`), automatisch dazumergen — als
    // sichtbar. So bekommen Bestandsuser neue Features ohne aktive Aktion.
    const layoutIds = new Set((layout.widgets || []).map(w => w.id));
    const def = defaultLayoutForRole(currentRole);
    const merged = [
      ...(layout.widgets || []),
      ...def.widgets.filter(w => !layoutIds.has(w.id)),
    ];
    return merged.filter(w => {
      if (!w.visible) return false;
      const def = WIDGETS[w.id];
      if (!def) return false;
      if (!def.visibleFor.includes(currentRole)) return false;
      // Optionaler Render-Filter — z.B. Reorder-Alarme nur wenn welche da sind.
      if (typeof def.shouldRender === "function" && !def.shouldRender(sharedProps)) return false;
      return true;
    });
  }


  return {
    storeStatusLabels,
    setStoreStatusLabels,
    showChannelsConfig,
    setShowChannelsConfig,
    showStatusLabelsConfig,
    setShowStatusLabelsConfig,
    expandedChannelId,
    setExpandedChannelId,
    currentView,
    setCurrentView,
    currentMode,
    setCurrentMode,
    activeSessions,
    setActiveSessions,
    storeDashboardLayout,
    setStoreDashboardLayout,
    showCustomizeDashboard,
    setShowCustomizeDashboard,
    dashboardEditMode,
    setDashboardEditMode,
    showAuditLog,
    setShowAuditLog,
    auditEntries,
    setAuditEntries,
    auditFilter,
    setAuditFilter,
    auditLoading,
    setAuditLoading,
    auditHasMore,
    setAuditHasMore,
    showScanner,
    setShowScanner,
    scannerState,
    setScannerState,
    scannerError,
    setScannerError,
    lastMatchedOrder,
    setLastMatchedOrder,
    lastUnmatchedCode,
    setLastUnmatchedCode,
    showMembers,
    setShowMembers,
    membersList,
    setMembersList,
    invitationsList,
    setInvitationsList,
    memberSheetTab,
    setMemberSheetTab,
    newInviteRole,
    setNewInviteRole,
    createdInviteCode,
    setCreatedInviteCode,
    confirmRemoveUserId,
    setConfirmRemoveUserId,
    memberLoading,
    setMemberLoading,
    orders,
    setOrders,
    hasMoreOrders,
    setHasMoreOrders,
    ordersCursor,
    setOrdersCursor,
    showSheet,
    setShowSheet,
    showDetail,
    setShowDetail,
    detailOrder,
    setDetailOrder,
    detailDeleteConfirm,
    setDetailDeleteConfirm,
    form,
    setForm,
    tabIndex,
    setTabIndex,
    filterKanal,
    setFilterKanal,
    showKanalFilter,
    setShowKanalFilter,
    search,
    setSearch,
    showSearch,
    setShowSearch,
    trackingConfirm,
    setTrackingConfirm,
    showSettings,
    setShowSettings,
    confirmDeleteStoreOpen,
    setConfirmDeleteStoreOpen,
    filterPickerOpen,
    setFilterPickerOpen,
    sortBy,
    setSortBy,
    dateRange,
    setDateRange,
    sendungOrder,
    setSendungOrder,
    showStats,
    setShowStats,
    chartMode,
    setChartMode,
    settingsDragY,
    setSettingsDragY,
    goal,
    setGoal,
    showGoalEdit,
    setShowGoalEdit,
    showGuvDetails,
    setShowGuvDetails,
    batches,
    setBatches,
    showLager,
    setShowLager,
    showCatalog,
    setShowCatalog,
    showCalculator,
    setShowCalculator,
    lagerProductId,
    setLagerProductId,
    lagerDragY,
    setLagerDragY,
    editBatchId,
    setEditBatchId,
    newBatchOpen,
    setNewBatchOpen,
    nbName,
    setNbName,
    nbStueck,
    setNbStueck,
    nbInvest,
    setNbInvest,
    editPool,
    setEditPool,
    collapsedBatches,
    setCollapsedBatches,
    editChannelInBatch,
    setEditChannelInBatch,
    newChannelOpen,
    setNewChannelOpen,
    confirmNoSendung,
    setConfirmNoSendung,
    confirmRetoure,
    setConfirmRetoure,
    shipChoice,
    setShipChoice,
    manualShip,
    setManualShip,
    helperSuccessBanner,
    setHelperSuccessBanner,
    confirmUnretoure,
    setConfirmUnretoure,
    collapsedMonths,
    setCollapsedMonths,
    archive,
    setArchive,
    showArchive,
    setShowArchive,
    archiveDragY,
    setArchiveDragY,
    statsDragY,
    setStatsDragY,
    confirmEmptyArchive,
    setConfirmEmptyArchive,
    lastKanal,
    setLastKanal,
    loaded,
    setLoaded,
    listVisible,
    setListVisible,
    slideDir,
    setSlideDir,
    saveState,
    setSaveState,
    searchRef,
    inputRef,
    listRef,
    labelUploadInputRef,
    pendingLabelOrderIdRef,
    triggerLabelUpload,
    tabIndexRef,
    touchStartX,
    switchingRef,
    switchTabRef,
    saveTimerRef,
    settingsStartY,
    lagerStartY,
    archiveStartY,
    statsStartY,
    scannerControlsRef,
    lastScanCodeRef,
    lastScanTimeRef,
    overlayTimerRef,
    can,
    getChannelLabel,
    getStatusLabel,
    getActiveChannels,
    getAllChannels,
    getChannelFinance,
    getOrderRevenue,
    getOrderShipping,
    getOrderFee,
    getOrderTotals,
    getOrderCogs,
    getOrderProfit,
    products,
    setProducts,
    channels,
    setChannels,
    apiProductCreate,
    apiProductUpdate,
    apiProductArchive,
    apiChannelCreate,
    apiChannelUpdate,
    apiChannelDelete,
    changeOrderChannel,
    apiSetProductChannelPrices,
    apiGetProductChannelPrices,
    slugifyChannelName,
    saveStoreConfig,
    loadMembersAndInvitations,
    createInvitation,
    revokeInvitation,
    changeMemberRole,
    removeMember,
    destroyCurrentStore,
    leaveCurrentStore,
    resetStoreState,
    loadMyDashboardLayout,
    saveDashboardLayout,
    orderFromDb,
    orderToDb,
    batchFromDb,
    getBatchStock,
    getProductStock,
    getProductCostPreview,
    getBatchChannelStock,
    channelAlarmLevel,
    getActiveAlarms,
    loadAuditLog,
    describeAuditEntry,
    formatRelative,
    apiOrderCreate,
    apiOrderUpdate,
    apiOrderArchive,
    apiOrderRestore,
    apiOrderEmptyArchive,
    apiBatchCreate,
    apiBatchUpdate,
    apiDeliveryCreate,
    loadStoreData,
    loadMoreOrders,
    switchTab,
    openNew,
    closeSheet,
    save,
    openDetail,
    openPanel,
    closeDetail,
    updateOrder,
    setLabelForOrder,
    openLabel,
    deleteFromDetail,
    deleteOrder,
    restoreFromArchive,
    purgeFromArchive,
    emptyArchive,
    confirmRetoureNow,
    confirmUnretoureNow,
    toggleRetoureErhalten,
    exportCSV,
    handleWidgetNavigate,
    getVisibleWidgets,
    getBatchForOrder,
    getActiveBatch,
    userEmail,
    helperMode,
    toggleHelperMode,
    isDuplicate,
    t,
    glass,
    inp,
    showToast,
    filterStatus,
    totalStueckzahl,
    totalInvestment,
    totalReserviert,
    totalUnreserved,
    effGoal,
    effInvestment,
    dateRangeCutoff,
    passesDateRange,
    filtered,
    groupedFiltered,
    MONTHS_DE,
    groupByMonth,
    counts,
    revenue,
    tabColors,
    pillLeft,
    pillWidth,
    o,
    detailInline,
    sheetsAsPopup,
    dRight,
    desktopRightPanelMarginTop,
    desktopRightPanel,
    desktopCenterPopup
  };
}
